import { Popover } from 'antd';
import { groupBy, initial } from 'lodash';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { LinkButton, Button } from 'components/atoms/button';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Control, Select, Option, SingleValue } from 'components/atoms/select';
import {
  DropdownIndicator,
  Menu,
} from 'components/atoms/select/popover/trigger-components';
import { isRelatedEl } from 'components/molecules/new-task-popup/is-related-element';
import { SalesforceResourceMappingLocalData } from '../../types';
import { VirtualizedMenuList } from '../../virtualised-list';
import { RocketlaneFieldValue } from './rocketlane-field-value';
import { SalesforceFieldValue } from './salesforce-field-value';
import './index.css';

type DefaultProps = {
  triggerLabel?: JSX.Element | string;
  value?: SingleValue;
  type: 'salesforce' | 'rocketlane';
  loading?: boolean;
};

type PopoverSelectProps = DefaultProps & {
  onVisibilityChange?: (value: boolean) => void;
  options?: Option[];
  onChange?: (changed?: SingleValue) => void;
  fieldObject: SalesforceResourceMappingLocalData;
};

type TriggerRendererProps = DefaultProps & {
  onTriggerClick: () => void;
};

export const SelectContainer = styled.div`
  height: 350px;
  width: 272px;
`;

const CustomButton = styled(Button)`
  padding: auto 0px;
`;

const TriggerRenderer = ({
  triggerLabel,
  onTriggerClick,
  value,
  type = 'rocketlane',
  loading,
}: TriggerRendererProps) => {
  if (value) {
    switch (type) {
      case 'salesforce': {
        return (
          <LinkButton type="link" onClick={onTriggerClick} loading={loading}>
            <SalesforceFieldValue label={value.label} />
          </LinkButton>
        );
      }

      case 'rocketlane': {
        return (
          <LinkButton type="link" onClick={onTriggerClick} loading={loading}>
            <RocketlaneFieldValue label={value.label} />
          </LinkButton>
        );
      }

      default: {
        return (
          <LinkButton type="link" onClick={onTriggerClick} loading={loading}>
            {value.label}
          </LinkButton>
        );
      }
    }
  }

  return (
    <CustomButton type="link" onClick={onTriggerClick} loading={loading}>
      {triggerLabel ?? <FormattedMessage id="add_field" />}
    </CustomButton>
  );
};

type OptionArray = {
  label: string;
  options: Option[];
};

const selectStyles = {
  menu: () => ({
    boxShadow: 'none',
    flex: 1,
    overflow: 'hidden',
    marginTop: 'var(--spacing-05) !important',
  }),
  menuList: (provided) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
    maxHeight: initial,
    height: '100%',
  }),
  control: (provided) => ({ ...provided, margin: '0 var(--spacing-05)' }),
  group: (provided) => ({ ...provided, paddingTop: 0, paddingBottom: 0 }),
  groupHeading: (provided) => ({ ...provided, marginBottom: 0, flex: 1 }),
  option: (provided) => ({
    ...provided,
    paddingLeft: 'var(--spacing-05)',
    paddingRight: 'var(--spacing-05)',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    display: 'flex !important',
  }),
};

export const PopoverSelect = ({
  type,
  value,
  options,
  loading,
  onChange,
  triggerLabel,
  onVisibilityChange,
  fieldObject,
}: PopoverSelectProps) => {
  const [visible, setVisible] = useState(false);
  const [searchedText, setSearchedText] = useState<string>('');

  const handleVisibilityToggle = (currentVisibility: boolean) => {
    setVisible(currentVisibility);
    onVisibilityChange?.(currentVisibility);
  };

  const onSelection = (props) => {
    if (onChange) {
      onChange(props);
    }
    handleVisibilityToggle(false);
  };

  const tempObj = groupBy(options, 'parent');

  let groupedOptions: OptionArray[] = Object.keys(tempObj).map((key) => {
    return {
      label: key,
      options: [
        ...tempObj[key].map((option) => {
          return {
            ...option,
            label:
              option.label.indexOf(']') > -1
                ? option.label.split(']')[1].trim()
                : option.label,
          };
        }),
      ],
    };
  });

  const nativeFields: OptionArray | undefined = groupedOptions.find(
    (option) => option.label === fieldObject.externalResourceType,
  );

  const rocketlaneFields: OptionArray | undefined = groupedOptions.find(
    (option) => option.label === 'Rocketlane',
  );

  if (nativeFields !== undefined) {
    groupedOptions = [
      nativeFields,
      ...groupedOptions.filter(
        (option) => option.label !== fieldObject.externalResourceType,
      ),
    ];
  } else if (rocketlaneFields !== undefined) {
    groupedOptions = [
      rocketlaneFields,
      ...groupedOptions.filter((option) => option.label !== 'Rocketlane'),
    ];
  }

  return (
    <OutsideClickHandler
      allowed={isRelatedEl}
      onOutsideClick={() => handleVisibilityToggle(false)}
    >
      <Popover
        overlayClassName="popover"
        placement="bottomLeft"
        visible={visible}
        content={
          <SelectContainer>
            <Select
              menuIsOpen
              autoFocus
              isLoading={loading}
              hideSelectedOptions={false}
              controlShouldRenderValue={false}
              components={{
                ClearIndicator: null,
                IndicatorSeparator: null,
                DropdownIndicator,
                Control,
                Menu,
                MenuList: VirtualizedMenuList,
              }}
              formatOptionLabel={
                type === 'salesforce'
                  ? SalesforceFieldValue
                  : RocketlaneFieldValue
              }
              isClearable
              placeholder={<FormattedMessage id="placeholder.search" />}
              tabSelectsValue={false}
              styles={selectStyles}
              options={groupedOptions}
              grouped
              value={value}
              onChange={onSelection}
              onBlur={() => handleVisibilityToggle(false)}
              searchedText={searchedText}
              onInputChange={(input) => setSearchedText(input)}
            />
          </SelectContainer>
        }
      >
        <TriggerRenderer
          loading={loading}
          type={type}
          triggerLabel={triggerLabel}
          value={value}
          onTriggerClick={() => handleVisibilityToggle(true)}
        />
      </Popover>
    </OutsideClickHandler>
  );
};
